import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Clock, Calendar, TrendingUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface NewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  article: {
    title: string;
    summary: string;
    fullContent?: string;
    imageUrl: string;
    articleUrl: string;
    category: string;
    publishedDate: string;
    readTime?: string;
    source?: string;
  } | null;
}

/* Estilo customizado para scrollbar */
<style>
{`
  .scrollbar-thin::-webkit-scrollbar {
    width: 8px;
  }
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #0f172a;
    border-radius: 4px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #334155;
    border-radius: 4px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #475569;
  }
`}
</style>

const NewsModal: React.FC<NewsModalProps> = ({ isOpen, onClose, article }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  if (!article) return null;

  const categoryColorsDark: Record<string, string> = {
    'Investimentos': 'bg-blue-500/20 text-blue-300 border-blue-500/50',
    'Economia': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50',
    'Finanças Pessoais': 'bg-purple-500/20 text-purple-300 border-purple-500/50',
    'Notícias': 'bg-orange-500/20 text-orange-300 border-orange-500/50',
    'Dicas': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50'
  };

  const categoryColorsLight: Record<string, string> = {
    'Investimentos': 'bg-blue-100 text-blue-700 border-blue-300',
    'Economia': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Finanças Pessoais': 'bg-purple-100 text-purple-700 border-purple-300',
    'Notícias': 'bg-orange-100 text-orange-700 border-orange-300',
    'Dicas': 'bg-yellow-100 text-yellow-700 border-yellow-300'
  };

  const categoryColors = isDark ? categoryColorsDark : categoryColorsLight;
  const categoryStyle = categoryColors[article.category] || (isDark ? 'bg-slate-500/20 text-slate-300 border-slate-500/50' : 'bg-slate-100 text-slate-700 border-slate-300');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className={`rounded-2xl shadow-2xl border overflow-hidden transition-colors ${
                isDark 
                  ? 'bg-slate-900 border-slate-700' 
                  : 'bg-white border-teal-200'
              }`}>
                {/* Header com Imagem */}
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    isDark 
                      ? 'bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent'
                      : 'bg-gradient-to-t from-white via-white/60 to-transparent'
                  }`} />
                  
                  {/* Close Button */}
                  <button
                    onClick={onClose}
                    className={`absolute top-4 right-4 p-2 backdrop-blur-sm rounded-full transition-colors ${
                      isDark
                        ? 'bg-slate-900/80 hover:bg-slate-800'
                        : 'bg-white/80 hover:bg-white'
                    }`}
                  >
                    <X className={isDark ? 'text-white' : 'text-slate-900'} size={24} />
                  </button>

                  {/* Category Badge */}
                  <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold border ${categoryStyle} backdrop-blur-sm`}>
                    {article.category}
                  </div>

                  {/* Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h2 className={`text-3xl md:text-4xl font-black mb-3 leading-tight ${
                      isDark ? 'text-white' : 'text-slate-900'
                    }`}>
                      {article.title}
                    </h2>
                    
                    {/* Meta Info */}
                    <div className={`flex flex-wrap items-center gap-4 text-sm ${
                      isDark ? 'text-slate-300' : 'text-slate-700'
                    }`}>
                      {article.source && (
                        <span className="flex items-center gap-1">
                          <TrendingUp size={16} />
                          {article.source}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar size={16} />
                        {article.publishedDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={16} />
                        {article.readTime || '5 min'} de leitura
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content - Scroll otimizado */}
                <div className="overflow-y-auto max-h-[calc(100vh-22rem)] md:max-h-[calc(90vh-20rem)] scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
                  <div className="p-6 md:p-8">
                    {/* Summary Box */}
                    <div className={`mb-6 p-5 rounded-xl border transition-colors ${
                      isDark
                        ? 'bg-gradient-to-br from-indigo-900/30 via-indigo-900/20 to-transparent border-indigo-500/30'
                        : 'bg-gradient-to-br from-indigo-50 via-indigo-100/50 to-transparent border-indigo-300'
                    }`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-1 h-6 rounded-full ${
                          isDark 
                            ? 'bg-gradient-to-b from-indigo-400 to-purple-500'
                            : 'bg-gradient-to-b from-indigo-600 to-purple-700'
                        }`}></div>
                        <h3 className={`text-sm font-black uppercase tracking-wider ${
                          isDark ? 'text-indigo-400' : 'text-indigo-700'
                        }`}>
                          📌 Resumo Executivo
                        </h3>
                      </div>
                      <p className={`leading-relaxed text-base ${
                        isDark ? 'text-slate-200' : 'text-slate-700'
                      }`}>
                        {article.summary}
                      </p>
                    </div>

                    {/* Expanded Content */}
                    <div className={`space-y-6 leading-relaxed ${
                      isDark ? 'text-slate-300' : 'text-slate-700'
                    }`}>
                      {/* O que você precisa saber */}
                      <section>
                        <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                          isDark ? 'text-white' : 'text-slate-900'
                        }`}>
                          <span className="text-2xl">💡</span>
                          O que você precisa saber
                        </h4>
                        <div className={`space-y-4 pl-4 border-l-2 ${
                          isDark ? 'border-indigo-500/30' : 'border-indigo-400'
                        }`}>
                          <p className={`leading-relaxed ${
                            isDark ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                            {article.summary}
                          </p>
                          <p className={`leading-relaxed ${
                            isDark ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                            Este conteúdo aborda temas relevantes do mercado financeiro, trazendo análises de especialistas e dados atualizados para ajudar você a tomar decisões mais informadas sobre suas finanças.
                          </p>
                        </div>
                      </section>

                      {/* Principais Destaques */}
                      <section className={`rounded-xl p-5 border transition-colors ${
                        isDark 
                          ? 'bg-slate-800/30 border-slate-700/50'
                          : 'bg-slate-50 border-slate-200'
                      }`}>
                        <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                          isDark ? 'text-white' : 'text-slate-900'
                        }`}>
                          <span className="text-2xl">⭐</span>
                          Principais Destaques
                        </h4>
                        <ul className="space-y-3">
                          <li className="flex gap-3">
                            <span className={isDark ? 'text-indigo-400 mt-1' : 'text-indigo-600 mt-1'}>▪</span>
                            <span>Análise aprofundada sobre tendências do mercado financeiro e oportunidades de investimento</span>
                          </li>
                          <li className="flex gap-3">
                            <span className={isDark ? 'text-indigo-400 mt-1' : 'text-indigo-600 mt-1'}>▪</span>
                            <span>Insights valiosos de especialistas e analistas certificados do setor</span>
                          </li>
                          <li className="flex gap-3">
                            <span className={isDark ? 'text-indigo-400 mt-1' : 'text-indigo-600 mt-1'}>▪</span>
                            <span>Impactos diretos na economia brasileira e como isso afeta seu bolso</span>
                          </li>
                          <li className="flex gap-3">
                            <span className={isDark ? 'text-indigo-400 mt-1' : 'text-indigo-600 mt-1'}>▪</span>
                            <span>Estratégias práticas para aplicar no seu planejamento financeiro pessoal</span>
                          </li>
                          <li className="flex gap-3">
                            <span className={isDark ? 'text-indigo-400 mt-1' : 'text-indigo-600 mt-1'}>▪</span>
                            <span>Contexto histórico e comparações com cenários anteriores do mercado</span>
                          </li>
                        </ul>
                      </section>

                      {/* Por que isso importa */}
                      <section>
                        <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                          isDark ? 'text-white' : 'text-slate-900'
                        }`}>
                          <span className="text-2xl">🎯</span>
                          Por que isso importa para você
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className={`p-4 rounded-lg border transition-colors ${
                            isDark 
                              ? 'bg-emerald-900/20 border-emerald-500/30'
                              : 'bg-emerald-50 border-emerald-200'
                          }`}>
                            <h5 className={`font-bold mb-2 ${
                              isDark ? 'text-emerald-400' : 'text-emerald-700'
                            }`}>💰 Para seus investimentos</h5>
                            <p className={`text-sm ${
                              isDark ? 'text-slate-400' : 'text-slate-600'
                            }`}>
                              Entenda como as tendências do mercado podem afetar sua carteira e descubra novas oportunidades de diversificação.
                            </p>
                          </div>
                          <div className={`p-4 rounded-lg border transition-colors ${
                            isDark 
                              ? 'bg-blue-900/20 border-blue-500/30'
                              : 'bg-blue-50 border-blue-200'
                          }`}>
                            <h5 className={`font-bold mb-2 ${
                              isDark ? 'text-blue-400' : 'text-blue-700'
                            }`}>📊 Para seu planejamento</h5>
                            <p className={`text-sm ${
                              isDark ? 'text-slate-400' : 'text-slate-600'
                            }`}>
                              Ajuste suas metas financeiras com base em informações atualizadas e projeções de especialistas.
                            </p>
                          </div>
                          <div className={`p-4 rounded-lg border transition-colors ${
                            isDark 
                              ? 'bg-purple-900/20 border-purple-500/30'
                              : 'bg-purple-50 border-purple-200'
                          }`}>
                            <h5 className={`font-bold mb-2 ${
                              isDark ? 'text-purple-400' : 'text-purple-700'
                            }`}>🔍 Para suas decisões</h5>
                            <p className={`text-sm ${
                              isDark ? 'text-slate-400' : 'text-slate-600'
                            }`}>
                              Tome decisões mais embasadas sobre quando investir, poupar ou ajustar seu orçamento mensal.
                            </p>
                          </div>
                          <div className={`p-4 rounded-lg border transition-colors ${
                            isDark 
                              ? 'bg-orange-900/20 border-orange-500/30'
                              : 'bg-orange-50 border-orange-200'
                          }`}>
                            <h5 className={`font-bold mb-2 ${
                              isDark ? 'text-orange-400' : 'text-orange-700'
                            }`}>📚 Para seu conhecimento</h5>
                            <p className={`text-sm ${
                              isDark ? 'text-slate-400' : 'text-slate-600'
                            }`}>
                              Desenvolva sua educação financeira com conteúdo de qualidade e linguagem acessível.
                            </p>
                          </div>
                        </div>
                      </section>

                      {/* Contexto e Aprofundamento */}
                      <section>
                        <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                          isDark ? 'text-white' : 'text-slate-900'
                        }`}>
                          <span className="text-2xl">📖</span>
                          Entenda o contexto
                        </h4>
                        <div className={`space-y-4 ${
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        }`}>
                          <p className="leading-relaxed">
                            O mercado financeiro brasileiro passa por constantes transformações, influenciado por fatores econômicos nacionais e internacionais. Acompanhar essas mudanças é essencial para quem deseja construir patrimônio de forma consistente e proteger seu poder de compra.
                          </p>
                          <p className="leading-relaxed">
                            Especialistas recomendam diversificação de investimentos, acompanhamento regular das taxas de juros (Selic), inflação (IPCA), e análise de cenários macroeconômicos antes de tomar decisões financeiras importantes.
                          </p>
                          <div className={`p-4 rounded border-l-4 transition-colors ${
                            isDark
                              ? 'bg-yellow-900/10 border-yellow-500'
                              : 'bg-yellow-50 border-yellow-500'
                          }`}>
                            <p className={`text-sm ${
                              isDark ? 'text-yellow-200/90' : 'text-yellow-900'
                            }`}>
                              <strong className="font-bold">💡 Dica importante:</strong> Sempre consulte profissionais certificados antes de fazer investimentos significativos. Este conteúdo tem caráter informativo e educacional.
                            </p>
                          </div>
                        </div>
                      </section>

                      {/* Próximos passos */}
                      <section className={`rounded-xl p-5 border transition-colors ${
                        isDark
                          ? 'bg-gradient-to-br from-slate-800/50 to-slate-900/50 border-slate-700'
                          : 'bg-gradient-to-br from-slate-50 to-white border-slate-200'
                      }`}>
                        <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                          isDark ? 'text-white' : 'text-slate-900'
                        }`}>
                          <span className="text-2xl">🚀</span>
                          Próximos passos recomendados
                        </h4>
                        <ol className="space-y-3">
                          <li className="flex gap-3">
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                              isDark ? 'bg-indigo-500' : 'bg-indigo-600'
                            }`}>1</span>
                            <span>Analise como este tema se relaciona com suas metas financeiras atuais</span>
                          </li>
                          <li className="flex gap-3">
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                              isDark ? 'bg-indigo-500' : 'bg-indigo-600'
                            }`}>2</span>
                            <span>Revise seu orçamento no Grana em Dia e identifique oportunidades de ajuste</span>
                          </li>
                          <li className="flex gap-3">
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                              isDark ? 'bg-indigo-500' : 'bg-indigo-600'
                            }`}>3</span>
                            <span>Consulte um especialista se estiver considerando mudanças significativas</span>
                          </li>
                          <li className="flex gap-3">
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                              isDark ? 'bg-indigo-500' : 'bg-indigo-600'
                            }`}>4</span>
                            <span>Continue acompanhando notícias atualizadas aqui no Fique por Dentro</span>
                          </li>
                        </ol>
                      </section>

                      {/* Footer discreto com fonte */}
                      <div className={`pt-6 mt-6 border-t transition-colors ${
                        isDark ? 'border-slate-700/50' : 'border-slate-200'
                      }`}>
                        <p className={`text-xs text-center mb-3 ${
                          isDark ? 'text-slate-500' : 'text-slate-500'
                        }`}>
                          Fonte: <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>{article.source || 'Conteúdo Educacional'}</span>
                        </p>
                        <div className="flex justify-center">
                          <a
                            href={article.articleUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-xs inline-flex items-center gap-1 transition-colors ${
                              isDark 
                                ? 'text-slate-500 hover:text-indigo-400'
                                : 'text-slate-600 hover:text-teal-600'
                            }`}
                          >
                            <ExternalLink size={12} />
                            Ver matéria completa na fonte original
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NewsModal;
